import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { BookOpen, ChevronRight, Play, Compass, Radio, Calendar, CalendarDays, CalendarCheck, ArrowLeft, Clock, BadgeCheck, MessageCircle, Users } from "lucide-react";
import { STATUS_CONFIG, CATEGORY_COLORS } from "@/lib/live-constants";

function getDaysUntilSeal(subscriptionStartedAt) {
  if (!subscriptionStartedAt) return null;
  const target = new Date(new Date(subscriptionStartedAt).getTime() + 30 * 24 * 60 * 60 * 1000);
  const diff = Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube: watch, youtu.be, embed, live
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  // Para qualquer outra URL, tenta incorporar diretamente no iframe
  return url;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function CarrosselEncontros({ liveLessons, onSelect }) {
  const scrollRef = useRef(null);

  function scrollLeft() {
    scrollRef.current?.scrollBy({ left: -240, behavior: 'smooth' });
  }
  function scrollRight() {
    scrollRef.current?.scrollBy({ left: 240, behavior: 'smooth' });
  }

  return (
    <div className="relative group">
      {/* Seta esquerda — desktop only */}
      <button
        onClick={scrollLeft}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', color: '#fff' }}
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
      </button>

      {/* Container scroll */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto hide-scrollbar py-2 -my-2"
        style={{ scrollSnapType: 'x mandatory', scrollBehavior: 'smooth' }}
      >
        {liveLessons.map(lesson => {
          const statusKey = lesson.live_status || "upcoming";
          const sc = STATUS_CONFIG[statusKey];
          const d = lesson.live_date ? new Date(lesson.live_date) : null;
          const dayNum = d ? d.getDate() : null;
          const monthShort = d ? d.toLocaleString("pt-BR", { month: "short" }).replace(".", "").toUpperCase() : null;
          const timeStr = d ? d.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null;
          return (
            <div
              key={lesson.id}
              className="flex-shrink-0 w-[180px] sm:w-[220px] h-[240px] sm:h-[280px] rounded-2xl relative overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.03]"
              style={{ scrollSnapAlign: 'start' }}
              onClick={() => onSelect(lesson)}
            >
              {/* Imagem full ou fallback */}
              {lesson.image_url ? (
                <img src={lesson.image_url} alt={lesson.title} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0" style={{ background: sc.gradient }}>
                  <Radio className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 text-white opacity-20" />
                </div>
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(transparent 35%, rgba(0,0,0,0.85) 100%)' }} />

              {/* Chip flutuante: ao vivo OU data+hora */}
              {statusKey === "live" ? (
                <span
                  className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: sc.bg, color: sc.text }}
                >
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
                  {sc.label}
                </span>
              ) : d ? (
                <span
                  className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', color: '#fff' }}
                >
                  <Calendar className="w-3 h-3" />
                  {dayNum} {monthShort} · {timeStr}
                </span>
              ) : null}

              {/* Badge gravação — top-right */}
              {statusKey === "recording" && (
                <span
                  className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-semibold"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', color: '#EAFAF6' }}
                >
                  Gravação
                </span>
              )}

              {/* Content — bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-3.5 flex flex-col gap-1">
                {lesson.category && CATEGORY_COLORS[lesson.category] && (
                  <span
                    className="self-start px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                    style={{ background: CATEGORY_COLORS[lesson.category].bg, color: CATEGORY_COLORS[lesson.category].text }}
                  >
                    {lesson.category}
                  </span>
                )}
                <h3 className="text-white font-bold text-xs line-clamp-2 leading-snug" title={lesson.title}>
                  {lesson.title}
                </h3>
                {lesson.presenter && (
                  <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Apresentado por {lesson.presenter}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Seta direita — desktop only */}
      <button
        onClick={scrollRight}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', color: '#fff' }}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function Home() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recentProgress, setRecentProgress] = useState([]);
  const [liveLessons, setLiveLessons] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerLesson, setPlayerLesson] = useState(null);
  const [sealModalOpen, setSealModalOpen] = useState(false);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const u = await db.auth.me();
      if (!u) return;
      setUser(u);
      const daysLeft = getDaysUntilSeal(u.subscription_started_at);
      if (daysLeft === 0 && !localStorage.getItem("portus_seal_seen_" + u.id)) {
        setSealModalOpen(true);
      }
      const [profiles, prog, lives] = await Promise.all([
        db.entities.UserProfile.filter({ user_email: u.email }, "-created_date", 1),
        db.entities.UserProgress.filter({ user_email: u.email }, "-created_date", 5),
        db.entities.Lesson.filter({ type: "live", is_published: true }, "live_date"),
      ]);
      setProfile(profiles[0] || null);
      setRecentProgress(prog);
      const upcoming = (lives || []).filter(l => l.live_status !== "recording");
      setLiveLessons(upcoming.filter(l => l.category !== "Oficina"));
      setOficinas(upcoming.filter(l => l.category === "Oficina"));
    } catch (err) {
      console.error("Home loadData error:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#00DCA4', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const firstName = user?.full_name?.split(" ")[0] || "você";

  // ── Player view ──────────────────────────────────────────────────
  if (playerLesson) {
    const statusKey = playerLesson.live_status || "upcoming";
    const sc = STATUS_CONFIG[statusKey];
    const activeUrl = statusKey === "recording"
      ? (playerLesson.video_url || playerLesson.live_url)
      : playerLesson.live_url;
    const embedUrl = getEmbedUrl(activeUrl);

    return (
      <div className="max-w-4xl mx-auto space-y-5">
        <button
          onClick={() => setPlayerLesson(null)}
          className="flex items-center gap-1.5 text-sm font-medium"
          style={{ color: '#646C6F' }}
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para a Home
        </button>

        {/* Player */}
        <div className="rounded-2xl overflow-hidden shadow-md" style={{ background: '#1A1D1E' }}>
          <div className="flex items-center justify-between px-4 py-3 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0"
                style={{ background: sc.bg, color: sc.text }}
              >
                {statusKey === "live" && (
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
                )}
                {sc.label}
              </span>
              <p className="text-white text-sm font-medium truncate">{playerLesson.title}</p>
            </div>
          </div>

          {embedUrl ? (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={playerLesson.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#FEEDB4' }}>
                <Clock className="w-8 h-8" style={{ color: '#65541B' }} />
              </div>
              <p className="text-sm" style={{ color: '#99A6AB' }}>
                Aula ainda não aconteceu — fique atento!
              </p>
            </div>
          )}
        </div>

        {/* Info */}
        {playerLesson.description && (
          <div className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #E5E9EA' }}>
            <h2 className="font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#2E3233' }}>
              {playerLesson.title}
            </h2>
            {playerLesson.live_date && (
              <div className="flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5" style={{ color: '#99A6AB' }} />
                <span className="text-sm" style={{ color: '#646C6F' }}>
                  {new Date(playerLesson.live_date).toLocaleString("pt-BR", {
                    day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
            )}
            <p className="text-sm leading-relaxed" style={{ color: '#646C6F' }}>
              {playerLesson.description}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Home normal ──────────────────────────────────────────────────
  const daysLeft = getDaysUntilSeal(user?.subscription_started_at);
  const sealProgress = daysLeft !== null ? ((30 - daysLeft) / 30) * 100 : 0;

  function closeSealModal() {
    setSealModalOpen(false);
    if (user?.id) {
      localStorage.setItem("portus_seal_seen_" + user.id, "true");
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">

      {/* Hero */}
      <div className="rounded-2xl p-6 lg:p-8" style={{ background: 'linear-gradient(135deg, #00664C 0%, #008572 100%)' }}>
        <p className="text-sm font-medium mb-1" style={{ color: '#61E8C6' }}>Bem-vindo de volta,</p>
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {firstName} 👋
        </h1>
        <p className="text-sm" style={{ color: '#B0FCEA' }}>Continue sua jornada no Portüs Talent Pro.</p>
      </div>

      {/* Banner WhatsApp */}
      <a
        href="https://chat.whatsapp.com/CcFHXLAFkg65RcAkbxR2pG"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-4 p-4 rounded-2xl hover:shadow-md transition-shadow"
        style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', textDecoration: 'none' }}
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.2)' }}>
          <MessageCircle className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Entre no nosso grupo de avisos</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Receba notificações sobre aulas ao vivo, novos conteúdos, funcionalidades e mais.
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-white flex-shrink-0" />
      </a>

      {/* Selo Talent Pro countdown */}
      {daysLeft !== null && daysLeft > 0 && (
        <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: '#fff', border: '1px solid #00DCA4' }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EAFAF6' }}>
            <BadgeCheck className="w-5 h-5" style={{ color: '#00DCA4' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#2E3233' }}>
              Faltam <strong>{daysLeft} {daysLeft === 1 ? "dia" : "dias"}</strong> para desbloquear o seu Selo Talent Pro
            </p>
            <div className="mt-2 w-full h-2 rounded-full overflow-hidden" style={{ background: '#EAFAF6' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${sealProgress}%`, background: '#00DCA4' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal de parabéns — Selo Talent Pro */}
      {sealModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center space-y-4" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#EAFAF6' }}>
                <BadgeCheck className="w-12 h-12" style={{ color: '#00DCA4' }} />
              </div>
            </div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
              Parabéns, {firstName}!
            </h2>
            <p className="text-sm" style={{ color: '#646C6F', lineHeight: 1.7 }}>
              Você agora é um profissional Talent Pro. Seu selo atesta sua credibilidade e dedicação ao conhecimento. Você terá mais visibilidade para recrutadores.
            </p>
            <button
              onClick={closeSealModal}
              className="px-6 py-2.5 rounded-full text-sm font-bold"
              style={{ background: '#00DCA4', color: '#003326' }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          {
            label: "Ver Trilhas",
            description: "Aprenda no seu ritmo com trilhas guiadas por especialistas",
            icon: BookOpen,
            page: "Tracks",
            iconColor: '#00664C',
            iconBg: '#B0FCEA',
          },
          {
            label: "Teste Vocacional",
            description: "Descubra qual profissão da nova economia combina com você",
            icon: Compass,
            page: "VocationalTest",
            iconColor: '#008572',
            iconBg: '#D5F9D7',
          },
          {
            label: "Calendário de Encontros",
            description: "Veja todos os encontros agendados no mês em um só lugar",
            icon: CalendarDays,
            page: "EventCalendar",
            iconColor: '#003326',
            iconBg: '#E5FF52',
          },
        ].map(item => (
          <Link key={item.page} to={createPageUrl(item.page)}>
            <div
              className="flex items-center gap-4 p-4 rounded-2xl bg-white hover:shadow-md transition-shadow cursor-pointer"
              style={{ border: '1px solid #E5E9EA' }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: item.iconBg }}>
                <item.icon className="w-5 h-5" style={{ color: item.iconColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#2E3233' }}>{item.label}</p>
                <p className="text-xs mt-0.5" style={{ color: '#99A6AB' }}>{item.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C8CED0' }} />
            </div>
          </Link>
        ))}
      </div>

      {/* Próximos encontros ao vivo — Carrossel Netflix */}
      {liveLessons.length > 0 && (
        <div>
          <style>{`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>Próximos encontros ao vivo</h2>
            <Link to={createPageUrl("LiveLessons")} className="flex items-center gap-1 text-sm font-medium" style={{ color: '#00664C' }}>
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <CarrosselEncontros liveLessons={liveLessons} onSelect={setPlayerLesson} />
        </div>
      )}

      {/* Próximas oficinas */}
      {oficinas.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>Próximas oficinas</h2>
            <Link to={createPageUrl("Workshops")} className="flex items-center gap-1 text-sm font-medium" style={{ color: '#00664C' }}>
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {oficinas.slice(0, 4).map(o => {
              const d = o.live_date ? new Date(o.live_date) : null;
              const dateStr = d ? d.toLocaleDateString("pt-BR", { weekday: "short", day: "numeric", month: "short" }) : null;
              const timeStr = d ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null;
              return (
                <Link key={o.id} to={createPageUrl("Workshops")}>
                  <div
                    className="flex items-center gap-4 p-4 rounded-2xl bg-white hover:shadow-md transition-shadow cursor-pointer"
                    style={{ border: '1px solid #E5E9EA' }}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#D4F5ED' }}>
                      <CalendarCheck className="w-5 h-5" style={{ color: '#00664C' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: '#2E3233' }}>{o.title}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {dateStr && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: '#99A6AB' }}>
                            <Calendar className="w-3 h-3" /> {dateStr} · {timeStr}
                          </span>
                        )}
                        {o.max_participants && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: '#99A6AB' }}>
                            <Users className="w-3 h-3" /> {o.max_participants} vagas
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className="px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0"
                      style={{ background: '#00DCA4', color: '#003326' }}
                    >
                      Agendar
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
